'use client';

import { useTranslation } from 'next-i18next';

import { RefObject, useCallback, useEffect, useState } from 'react';

import { IconButton, useBreakpointValue } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons/linear';

import { useScrollPosition } from '@app/hooks/useScrollPosition';

export const ScrollTopButton = ({
    scrollableRef,
    threshold = 1,
    present = true,
}: {
    scrollableRef?: RefObject<HTMLElement>;
    threshold?: number;
    present?: boolean;
}) => {
    const { t } = useTranslation('common');
    const showAboveMd = useBreakpointValue({
        base: false,
        lg: true,
    });
    const scrollPosition = useScrollPosition(scrollableRef);
    const [containerHeight, setContainerHeight] = useState(0);
    const isWindow = scrollableRef === undefined && typeof window !== 'undefined';
    const setHeight = useCallback(() => {
        if (scrollableRef?.current) {
            setContainerHeight(scrollableRef.current.offsetHeight);
        } else if (isWindow) {
            setContainerHeight(window.innerHeight);
        }
    }, []);

    useEffect(() => {
        setHeight();
    }, [setHeight]);

    const buttonProps = {
        position: scrollableRef?.current ? 'absolute' : 'fixed',
        bottom: 6,
        right: 6,
        rounded: 'full',
        zIndex: 'docked',
        bgColor: 'white',
        size: 'lg',
        filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.12)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.10))',
    } as const;

    const onClick = useCallback(() => {
        (scrollableRef?.current || window).scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const shouldShow = present && scrollPosition >= threshold * containerHeight;

    return showAboveMd ? (
        <IconButton
            {...buttonProps}
            colorPalette={'gray'}
            variant={'outline'}
            onClick={onClick}
            opacity={shouldShow ? 1 : 0}
            transform={shouldShow ? 'scale(1)' : 'scale(0.8)'}
            transition="all 0.4s ease-out"
            pointerEvents={shouldShow ? 'auto' : 'none'}
            aria-label={`${t('scrollTopBtn')}`}
            _hover={{
                '& > svg': {
                    animation: 'subtle-bounce-up',
                },
            }}
        >
            <IconsLinear.ArrowUp3 />
        </IconButton>
    ) : null;
};
